import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from 'src/components/widgets/badge';
import { BasicButton } from 'src/components/widgets/buttons';
import IfCanAccess from 'src/components/widgets/ifCanAccess/ifCanAccess';
import { ContainerBase } from 'src/components/widgets/layouts';
import { PageHeader } from 'src/components/widgets/pageHeader';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { backofficeUrl, T } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import usePermissions from 'src/shared/hooks/usePermissions';
import styled from 'styled-components';
import { bulkImportColors } from './palette';

export type BulkImporterTaskState = 'WAITING' | 'PROCESSING' | 'COMPLETED' | 'ERROR' | 'COMPLETED_WITH_ERRORS';

export const ALL_TASK_STATES: BulkImporterTaskState[] = ['WAITING', 'PROCESSING', 'COMPLETED', 'ERROR', 'COMPLETED_WITH_ERRORS'];

export const stateLabel = (state: BulkImporterTaskState): string => {
    switch (state) {
        case 'WAITING':
            return T._('Waiting', 'Admin');
        case 'PROCESSING':
            return T._('Processing', 'Admin');
        case 'COMPLETED':
            return T._('Completed', 'Admin');
        case 'ERROR':
            return T._('Error', 'Admin');
        case 'COMPLETED_WITH_ERRORS':
            return T._('Completed with errors', 'Admin');
        default:
            return state;
    }
};

export const StateBadge: React.FC<{ state: BulkImporterTaskState }> = ({ state }) => {
    switch (state) {
        case 'WAITING':
            return <Badge variant='neutral'>{stateLabel(state)}</Badge>;
        case 'PROCESSING':
            return <Badge variant='info'>{stateLabel(state)}</Badge>;
        case 'COMPLETED':
            return <Badge variant='success'>{stateLabel(state)}</Badge>;
        case 'ERROR':
            return <Badge variant='danger'>{stateLabel(state)}</Badge>;
        case 'COMPLETED_WITH_ERRORS':
            return <Badge variant='warning'>{stateLabel(state)}</Badge>;
        default:
            return <Badge variant='neutral'>{state}</Badge>;
    }
};

export const formatElapsed = (ms: number | null | undefined): string => {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms} ms`;
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec} s`;
    const min = Math.floor(sec / 60);
    const remSec = sec % 60;
    if (min < 60) return `${min}m ${remSec}s`;
    const hr = Math.floor(min / 60);
    const remMin = min % 60;
    return `${hr}h ${remMin}m`;
};

export const sourceLabel = (source: string | null | undefined): string => {
    if (!source) return T._('Legacy', 'Admin');
    if (source === 'CSV_V3') return 'CSV';
    return source;
};

// ── Rollout-flag checks, WITHOUT the IAM permission ────────────────────────────────────────
// The 'BULK_IMPORT' permission is enforced separately by <BulkImportGate> (via <IfCanAccess>),
// so a permission-less IAM user gets the standard "request access" lock screen — aligned with
// the rest of the app — instead of the generic "not available" box, which is reserved for the
// rollout flag being off.

// Azure App Config 'bulkImport' rollout flag only.
export const useIsBulkImportFlagEnabled = (): boolean => {
    const { userInfo } = useAccountData();
    return useFeatureFlagWithConditions('bulkImport', {
        userId: userInfo?.userID?.toString()
    });
};

// 'bulkImport' rollout flag AND the separate 'bulkExport' rollout flag.
export const useIsBulkExportFlagEnabled = (): boolean => {
    const { userInfo } = useAccountData();
    const importFlag = useIsBulkImportFlagEnabled();
    const exportFlag = useFeatureFlagWithConditions('bulkExport', {
        userId: userInfo?.userID?.toString()
    });
    return importFlag && exportFlag;
};

// True when the Bulk Import feature is FULLY available to the current user: rollout flag AND the
// IAM 'BULK_IMPORT' permission (covers both import and export). Use this to show/hide ENTRY POINTS
// (cards, nav tabs, buttons) — a permission-less user should not see them. Page access itself is
// gated by <BulkImportGate>. Seller-owners (non-IAM users) implicitly satisfy the permission gate.
export const useIsBulkImportEnabled = (): boolean => {
    const flagEnabled = useIsBulkImportFlagEnabled();
    const { isAllowed } = usePermissions();
    return flagEnabled && isAllowed(['BULK_IMPORT']);
};

// True when the CSV EXPORT sub-feature is fully available: both rollout flags AND the
// 'BULK_IMPORT' permission.
export const useIsBulkExportEnabled = (): boolean => {
    const flagEnabled = useIsBulkExportFlagEnabled();
    const { isAllowed } = usePermissions();
    return flagEnabled && isAllowed(['BULK_IMPORT']);
};

const NotAvailableBox = styled.div`
    padding: 32px;
    text-align: center;
    color: ${bulkImportColors.textSubtle};
    font-size: 13px;
    background: ${bulkImportColors.headerBg};
    border-radius: 8px;
    margin-top: 16px;
`;

const NotAvailableActions = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
`;

export const BulkImportNotAvailable: React.FC = () => {
    const navigate = useNavigate();
    return (
        <ContainerBase>
            <PageHeader title={T._('Bulk import', 'Admin')} pageKey='bulk-import' isBackVisible />
            <NotAvailableBox>
                {T._('This feature is not available for your account.', 'Admin')}
                <NotAvailableActions>
                    <BasicButton type='button' onClick={() => navigate(backofficeUrl('/dashboard'))}>
                        {T._('Back to dashboard', 'Admin')}
                    </BasicButton>
                    <BasicButton type='button' onClick={() => navigate(backofficeUrl('/tools/CSVImporter'))}>
                        {T._('Use the legacy CSV importer', 'Admin')}
                    </BasicButton>
                </NotAvailableActions>
            </NotAvailableBox>
        </ContainerBase>
    );
};

// Page-access gate for every Bulk Import route. Two distinct outcomes, aligned with the rest
// of the app:
//   • rollout flag OFF         → "not available for your account" box (feature not in the plan)
//   • flag ON but IAM lacks it → standard <IfCanAccess> lock screen ("request access")
// Pass `requireExport` for the export wizard, which additionally needs the 'bulkExport' flag.
export const BulkImportGate: React.FC<React.PropsWithChildren<{ requireExport?: boolean }>> = ({ requireExport = false, children }) => {
    const importFlagEnabled = useIsBulkImportFlagEnabled();
    const exportFlagEnabled = useIsBulkExportFlagEnabled();
    const flagEnabled = requireExport ? exportFlagEnabled : importFlagEnabled;

    if (!flagEnabled) return <BulkImportNotAvailable />;
    return <IfCanAccess permission='BULK_IMPORT'>{children}</IfCanAccess>;
};
